import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useState } from "react";
import ChatshopLogo from 'assets/media/images/Chatshop-long-logo.svg';
import PagiiLogo from 'assets/media/images/Pagii-icon.png';

import LoginLayout from "./login.layout";

import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useRedux from "redux/useRedux";
import { AuthForgotPassword } from "redux/auth/auth.services";
import "react-toastify/dist/ReactToastify.css";
import { Error } from "@mui/icons-material";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { thunkDispatch } = useRedux();
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [email, setEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleForgotPassword = async (e: any) => {
    e.preventDefault();
    setIsSubmitted(true);

    if (email === "") {
      setErrorMsg("Email wajib diisi");
      return;
    }

    if (!executeRecaptcha) {
      setErrorMsg("Sistem keamanan belum siap. Silakan coba lagi.");
      return;
    }

    const token = await executeRecaptcha("forgot_password");
    if (!token) {
      setErrorMsg("Validasi sistem keamanan gagal.");
      return;
    }

    setCaptchaToken(token);
    setErrorMsg(null);

    thunkDispatch(AuthForgotPassword({ email }))
      .unwrap()
      .then((res: any) => {
        if (res && res.status === "error") {
          setErrorMsg(res.data || "Email tidak ditemukan atau terjadi kesalahan");
        } else {
          toast.success("Jika email terdaftar, Anda akan mendapatkan email instruksi reset password.");
          setTimeout(() => {
            navigate("/login");
          }, 3000);
        }
      })
      .catch(() => {
        setErrorMsg("Terjadi kesalahan sistem. Silakan coba lagi.");
      });
  };

  return (
    <LoginLayout title="Pagii Chatshop - Lupa Kata Sandi" fullScreen>
      <ToastContainer />
      <div className="flex h-screen w-full flex-col lg:flex-row overflow-hidden">
        <div className="relative hidden w-full lg:block lg:w-3/5 h-full overflow-hidden shrink-0">
          <img
            src="/login-cover-2.jpeg"
            alt="Login Illustration"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent flex flex-col justify-end p-10 lg:p-16">
            <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl p-6 lg:p-8 shadow-2xl max-w-md">
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="/ic-pagii-chatshop-small.svg"
                  alt="Chatshop Logo"
                  className="h-8 w-auto"
                />
                <span className="text-2xl font-bold text-white tracking-widest ">
                  CHATSHOP
                </span>
              </div>
              <h2 className="text-lg text-white font-medium leading-relaxed ">
                Solusi Terpadu Kelola Bisnis & Percakapan Pelanggan dalam Satu Platform yang Cerdas.
              </h2>
            </div>
          </div>
        </div>

        <div className="flex flex-1 w-full items-center justify-center bg-white p-8 lg:w-2/5 lg:p-12 overflow-y-auto">
          <div className="w-full max-w-md space-y-8">
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
              <div className="flex items-center gap-2.5 mb-8">
                <a href="https://chatshop.pagii.co/" target="_blank" rel="noopener noreferrer">
                  <img
                    src={ChatshopLogo}
                    alt="Chatshop"
                    className="h-12 w-auto"
                  />
                </a>
              </div>

              <h1 className="text-4xl font-bold tracking-tight text-slate-900">
                Lupa Kata Sandi
              </h1>
              <p className="mt-2 text-sm text-slate-400">
                Masukkan email Anda dan kami akan mengirimkan tautan untuk mereset kata sandi.
              </p>
            </div>

            {errorMsg && (
              <div
                className="bg-red-50 border-2 border-red-500 text-red-700 px-4 py-3 rounded-lg relative text-sm flex items-center gap-2"
                role="alert"
              >
                <Error className="text-xl" />
                <span className="block sm:inline">{errorMsg}</span>
              </div>
            )}
            
            <form onSubmit={handleForgotPassword} className="mt-10 space-y-6">
              <div className="group relative">
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrorMsg(null);
                  }}
                  className={`block w-full rounded-lg border-2 bg-transparent px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:border-brand focus:outline-none focus:ring-0 transition-all  ${isSubmitted && email === ""
                      ? "border-red-500"
                      : "border-slate-200"
                    }`}
                  placeholder="Email Anda"
                />
                <div className="h-4">
                  {isSubmitted && email === "" && (
                    <p className="mt-1 text-xs text-red-500 ">
                      Email tidak boleh kosong
                    </p>
                  )}
                </div>
              </div>



              <div className="flex justify-between items-center mt-6">
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-sm font-semibold text-brand hover:text-brand-dark transition-colors"
                >
                  Kembali ke Login
                </button>
                <button
                  type="submit"
                  className="px-8 rounded-lg bg-brand py-3 text-base font-bold text-white shadow-lg transition-all hover:bg-brand-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-dark active:scale-[0.98] "
                >
                  Kirim
                </button>
              </div>

              <div className="flex justify-end mt-4">
                <div className="fixed bottom-4">
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    <span>Powered by</span>
                    <a href="https://pagii.co/" target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-75">
                      <img src={PagiiLogo} alt="Pagii" className="h-4 w-auto inline" />
                    </a>
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </LoginLayout>
  );
};


const ForgotPasswordWithReCaptcha = () => (
  <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}>
    <ForgotPassword />
  </GoogleReCaptchaProvider>
);

export default ForgotPasswordWithReCaptcha;
